import * as React from 'react';
import { Paragraph } from 'components/src/typography';
import Dropdown, { Option } from 'components/src/dropdown';
import { getOr } from '@/utils/options';
import { MobilePackageType } from './Types';
import { InfoSetById } from '@/clients/queries/contentful/InfoSetById.graphql';
import { InfoSetByIdQuery, InfoSetByIdQueryVariables } from 'types/generated';
import TextWithInlineColor from '../TextWithInlineColor';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS } from '@contentful/rich-text-types';
import CardButton from '../Card/CardButton';
import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import { Log } from '@/logging/datadog';

type Props = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setMobilePackage: React.Dispatch<React.SetStateAction<MobilePackageType | null>>;
  mobilePackage: MobilePackageType | null;
  infoSetId: string;
};

// Value is the package id
const options: Option[] = [
  { value: 29223, label: '500 kr.' },
  { value: 28569, label: '1.000 kr.' },
  { value: 28571, label: '2.000 kr.' },
  { value: 28579, label: '3.000 kr.' },
  { value: 28583, label: '4.000 kr.' },
  { value: 28585, label: '5.000 kr.' },
  { value: 28977, label: '10.000 kr.' },
  { value: 28981, label: '15.000 kr.' },
];

const validatePackage = (
  value: Option | null,
  setMobilePackage: React.Dispatch<React.SetStateAction<MobilePackageType | null>>
) => {
  setMobilePackage({
    subTitle: 'Fylla á',
    label: `${getOr(value?.label, '')}`,
    price: getOr(Number(value?.label.replace(/[^0-9]/g, '')), 0),
    //Getting shown price to compare to price from server
    id: getOr(Number(value?.value), 0),
    selectedFromDropDown: true,
  });
};

const RichTextContainer = ({ data }: { data: Document }) => {
  return documentToReactComponents(data, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => {
        return <Paragraph classes="!text-base !leading-normal text-black-100 empty:h-[18px]">{children}</Paragraph>;
      },
    },
  });
};

const ChooseFrelsiPrice = ({ setShowModal, setMobilePackage, mobilePackage, infoSetId }: Props) => {
  const { loading, error, data } = useQuery<InfoSetByIdQuery, InfoSetByIdQueryVariables>(InfoSetById, {
    variables: { id: infoSetId },
  });

  const selectedValue = React.useMemo(() => {
    return mobilePackage?.selectedFromDropDown ? { value: mobilePackage.id, label: mobilePackage.label } : null;
  }, [mobilePackage]);

  if (error) {
    Log.error('Error in ChooseFrelsiPrice', error);
    return (
      <div
        className={clsx(
          'flex max-w-[1424px] flex-col items-center justify-center justify-items-center gap-lg self-stretch rounded-28 px-xl py-xbig text-center md:col-span-10'
        )}
      />
    );
  }

  const infoSet = data?.infoSet;

  return (
    <div
      className={clsx(
        'col-span-full grid justify-center rounded-28 bg-white-100 px-24 py-64 text-center md:col-span-10',
        { 'min-h-[100px]': loading }
      )}
    >
      {infoSet?.heading?.text && (
        <TextWithInlineColor
          heading={infoSet.heading.text.json}
          color={infoSet.heading.color}
          headingVariant={'small'}
        />
      )}
      <div className="mt-32 grid justify-center gap-16 sm:flex">
        <div className="mt-16 max-w-[600px]">
          <RichTextContainer data={infoSet?.descriptionMarkdown?.json} />
        </div>
        <div className="mt-32 grid justify-center gap-16 sm:flex">
          <div className="w-[208px]">
            <Dropdown
              key={selectedValue ? 'frelsi-valid' : 'veldu-frelsi'}
              id={'velja-frelsi'}
              placeholder={'Veldu upphæð'}
              options={options}
              onChange={(value) => {
                validatePackage(value, setMobilePackage);
              }}
              isSearchable={false}
              selectedOption={selectedValue}
            />
          </div>
          {infoSet?.button && (
            <CardButton
              buttonData={infoSet.button}
              onClick={() => setShowModal(true)}
              animateOnSuccess={false}
              autoWidth={true}
              isDisabled={!selectedValue}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default ChooseFrelsiPrice;
